import React from 'react';
import { TableColumnsProvider } from '@/shared/hooks/useTableColumns';
import { Column } from './ColumnFilter';

/**
 * Пропсы для компонента TableColumnsFilter
 */
interface TableColumnsFilterProps {
  /** Массив всех доступных столбцов с их метаданными */
  columns: Column[];
  /** Ключ для хранения настроек видимости столбцов в localStorage */
  storageKey: string;
  /** Массив ключей столбцов, которые должны быть видимы по умолчанию */
  defaultColumns?: string[];
  /** Дочерние компоненты */
  children?: React.ReactNode;
}

/**
 * Компонент для фильтрации столбцов таблицы
 *
 * Оборачивает дочерние компоненты в провайдер контекста TableColumnsProvider.
 * Не включает в себя компонент ColumnFilter, что позволяет размещать его в любом месте внутри контекста.
 *
 * @example
 * ```tsx
 * // Пример с размещением ColumnFilter в произвольном месте
 * <TableColumnsFilter
 *   columns={tableColumns}
 *   storageKey="my-table-columns"
 *   defaultColumns={['name', 'email']}
 * >
 *   <div className="flex justify-between">
 *     <div className="flex gap-4">
 *       <input type="text" placeholder="Search..." />
 *       <Select options={statusOptions} />
 *       <ColumnFilter />
 *     </div>
 *     <Button>Add New</Button>
 *   </div>
 *   <MyTable data={data} />
 * </TableColumnsFilter>
 * ```
 */
export const TableColumnsFilter: React.FC<TableColumnsFilterProps> = ({
  columns,
  storageKey,
  defaultColumns,
  children
}) => {
  // Если defaultColumns не указаны, используем все ключи из columns
  const defaultVisibleColumns = defaultColumns || columns.map(col => col.key);

  return (
    <TableColumnsProvider
      storageKey={storageKey}
      defaultColumns={defaultVisibleColumns}
      columns={columns}
    >
      {/* Просто передаем дочерние элементы без дополнительной обертки */}
      {children}
    </TableColumnsProvider>
  );
};
